import React from 'react';
import Icon from '../../../components/AppIcon';

const RecentActivity = ({ progressData, userAchievements }) => {
  // Combine all activities into a single timeline
  const getRecentActivities = () => {
    const activities = [];

    // Add progress updates
    progressData?.progress?.forEach(progress => {
      activities?.push({
        type: 'progress',
        icon: 'BookOpen',
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/20',
        title: progress?.status === 'completed' 
          ? `Completed "${progress?.plays?.title}"`
          : `Reading "${progress?.plays?.title}"`,
        description: `${progress?.completion_percentage}% complete - ${progress?.lines_completed} lines read`,
        timestamp: progress?.last_accessed_at || progress?.created_at,
        link: `/play-reading-interface?playId=${progress?.play_id}`
      });
    });

    // Add quiz attempts
    progressData?.quizAttempts?.forEach(attempt => {
      const passed = attempt?.passed;
      activities?.push({
        type: 'quiz',
        icon: passed ? 'CheckCircle' : 'Target',
        color: passed ? 'text-green-400' : 'text-yellow-400',
        bgColor: passed ? 'bg-green-500/20' : 'bg-yellow-500/20',
        title: passed ? 'Quiz Passed!' : 'Quiz Attempted',
        description: `Score: ${attempt?.score}% - ${attempt?.total_questions} questions in ${Math.round(attempt?.time_taken_seconds / 60)} minutes`,
        timestamp: attempt?.completed_at,
        link: '/quiz-interface'
      });
    });

    // Add achievements
    userAchievements?.forEach(achievement => {
      activities?.push({
        type: 'achievement',
        icon: 'Award',
        color: 'text-accent-purple',
        bgColor: 'bg-accent-purple/20',
        title: `Earned "${achievement?.achievements?.name}"!`,
        description: `+${achievement?.achievements?.xp_reward} XP - ${achievement?.achievements?.description}`,
        timestamp: achievement?.earned_at,
        link: '/student-progress-center'
      });
    });

    // Sort by timestamp (most recent first)
    return activities
      ?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
      ?.slice(0, 10);
  };

  const activities = getRecentActivities();

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-surface-lighter border border-border rounded-xl p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">Recent Activity</h2>
        <p className="text-text-secondary text-sm">
          Your latest achievements and progress updates
        </p>
      </div>

      {activities?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Activity" size={48} className="text-text-secondary mx-auto mb-3 opacity-50" />
          <p className="text-text-secondary">No recent activity yet</p>
          <p className="text-text-secondary text-sm mt-1">
            Start exploring plays to see your activity here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities?.map((activity, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 bg-surface-dark rounded-lg border border-border hover:border-accent-purple transition-all duration-300 group cursor-pointer"
              onClick={() => window.location.href = activity?.link}
            >
              {/* Activity Icon */}
              <div className={`p-3 rounded-lg ${activity?.bgColor} flex-shrink-0 group-hover:scale-110 transition-transform`}>
                <Icon name={activity?.icon} size={24} className={activity?.color} />
              </div>

              {/* Activity Details */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold mb-1 group-hover:text-accent-purple transition-colors">
                  {activity?.title}
                </h3>
                <p className="text-text-secondary text-sm mb-2">
                  {activity?.description}
                </p>
                <div className="flex items-center gap-2 text-xs text-text-secondary">
                  <Icon name="Clock" size={12} />
                  <span>{formatTimestamp(activity?.timestamp)}</span>
                </div>
              </div>

              {/* Arrow Icon */}
              <Icon 
                name="ChevronRight" 
                size={20} 
                className="text-text-secondary group-hover:text-accent-purple transition-colors flex-shrink-0" 
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;